import * as React from 'react';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import BackIcon from '@mui/icons-material/ArrowBack';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Box, Button, useTheme } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { LoadingButton } from '@mui/lab';
import { SvgIconComponent } from '@mui/icons-material';
import { FooterAction } from '../../interfaces';
import { important } from '../../utils/global/helper';

interface ButtonProps {
  text: string;
  icon?: SvgIconComponent;
  onClick?: () => void | Promise<void>;
  disabled?: boolean;
  type?: 'submit' | 'button';
  isLoading?: boolean;
  variant?: React.ComponentProps<typeof Button>['variant'];
}

type Props = {
  name: string;
  handleClose: () => void;
  moreActions?: FooterAction[];
  isDismissable?: boolean;
  hideCloseButton?: boolean;
  backButton?: boolean;
  submitButton?: ButtonProps;
  secondaryButton?: Omit<ButtonProps, 'type'>;
  noMargin?: boolean;
};

export default function ModalHeader({
  name,
  handleClose,
  moreActions,
  isDismissable = true,
  hideCloseButton = false,
  backButton,
  submitButton,
  secondaryButton,
  noMargin = false,
}: Props) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const Icon = backButton ? BackIcon : CloseIcon;

  const renderButtonIcon = (icon?: SvgIconComponent) => {
    if (!icon) return null;
    const ButtonIcon = icon;
    return (
      <ButtonIcon
        sx={{
          width: '16px',
        }}
      />
    );
  };

  return (
    <Box
      className="modal-header"
      sx={{
        marginBottom: noMargin ? important(0) : undefined,
      }}>
      {isDismissable && !hideCloseButton && (
        <button className="close-modal" type="button" onClick={handleClose}>
          <Icon />
        </button>
      )}
      <Typography variant="h2">{name}</Typography>
      {(secondaryButton || submitButton) && (
        <Box
          position="absolute"
          right={{
            xs: theme.spacing(2),
            md: theme.spacing(4),
          }}
          top={0}
          height="100%"
          alignItems="center"
          display={{
            xs: 'none',
            sm: 'flex',
          }}
          gap={{
            xs: 0.5,
            sm: 1,
            md: 1.5,
          }}>
          {secondaryButton && (
            <Button
              type="button"
              variant={secondaryButton.variant || 'link'}
              disabled={secondaryButton.disabled}
              onClick={secondaryButton.onClick}
              startIcon={renderButtonIcon(secondaryButton.icon)}
              sx={{
                borderRadius: '4px',
              }}>
              {secondaryButton.text}
            </Button>
          )}
          {submitButton && (
            <LoadingButton
              type={submitButton.type}
              variant={submitButton.variant || 'contained'}
              className="primary"
              disabled={submitButton.disabled}
              onClick={submitButton.onClick}
              loading={submitButton.isLoading}
              startIcon={renderButtonIcon(submitButton.icon)}>
              {submitButton.text}
            </LoadingButton>
          )}
        </Box>
      )}
      {moreActions && !!moreActions.length && (
        <>
          <Button
            id="basic-button"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            style={{
              color: 'black',
              padding: 0,
              margin: 0,
            }}
            className="more-actions"
            onClick={handleClick}>
            <MoreHorizIcon />
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}>
            {moreActions?.map(action => (
              <MenuItem
                key={action.label?.toString()}
                onClick={() => {
                  handleMenuClose();
                  action.action();
                }}>
                {action.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
}
